import { kebabCase } from 'lodash';
import React, {
  ChangeEventHandler,
  DetailedHTMLProps,
  FC,
  HTMLAttributes,
  InputHTMLAttributes,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Checkbox } from '../Form/Controls/Checkbox';
import { formatClassName, pickDataAttributes } from '../../utils';
import { SelectionLabel } from '../../core/components/SelectionControl/label';

type InputProps = Partial<Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'size'>>;
type ContainerProps = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> & DOMStringMap;

export type ToggleCheckboxProps = InputProps & {
  chakra?: string;
  checked: boolean;
  containerProps?: ContainerProps;
  hintText?: string;
  label?: string;
  leftToggle?: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onClick?: MouseEventHandler<HTMLDivElement>;
  size?: 'sm' | 'lg';
};

/**
 * React Toggle Checkbox components
 * @param {object}  props.defaultChecked   Initial checked state - can be overriden
 * @param {object}  props.checked          Uses onChange to override default behaviour
 * @param {string}  props.className        Layout design level customization - size: sm, lg, xl or round
 *
 * @example <caption>Usage: </caption>
 * //Default values
 * <ToggleCheckbox
 *    defaultChecked={true}
 *    size="sm"
 * />
 *
 * //Round large toggle checkbox with checked value override
 * <ToggleCheckbox
 *    checked={this.state.isChecked}
 *    onChange={() => this.setState({ isChecked: !this.state.isChecked} )} //Bad setstate example
 *    size="lg"
 * />
 */
export const ToggleCheckbox: FC<ToggleCheckboxProps> = props => {
  const {
    chakra = 'on',
    checked,
    className = '',
    containerProps,
    defaultChecked = false,
    disabled,
    hintText,
    id,
    label,
    leftToggle = false,
    onChange,
    size = 'sm',
    ...inputProps
  } = props;

  const input = useRef<HTMLInputElement>(null);

  const [checkedState, setChecked] = useState<boolean>(checked ?? defaultChecked);

  useEffect(() => {
    if (checked !== undefined) {
      setChecked(!!checked);
    }
  }, [checked]);

  /**
   * Click event handler for toggle checkbox
   * @param {object} event  Event
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const checkbox = input.current!;
    if (event.target !== checkbox) {
      event.preventDefault();
      checkbox.focus();
      checkbox.click();
      return;
    }

    setChecked(checked !== undefined ? checked : checkbox.checked);
  };

  const handleChakraClick = (event: any) => {
    setChecked(!checkedState);
    if (typeof onChange === 'function') {
      onChange(event);
    }
    if (typeof inputProps?.onClick === 'function') {
      inputProps.onClick(event);
    }
  };

  const dataAttributes = pickDataAttributes(props, 'toggle-checkbox');
  if (!dataAttributes['data-action']) {
    dataAttributes['data-action'] = label ? `toggle-${kebabCase(label)}` : 'toggle';
  }

  const classes = formatClassName([className, 'round', size]);
  const labelClasses = formatClassName(['toggle-checkbox-label', leftToggle ? 'm-l-10' : 'm-r-10']);

  const toggle = (
    <div className={`toggle--checkbox ${classes} ${disabled && 'disabled'}`} onClick={handleClick} {...dataAttributes}>
      <input
        {...inputProps}
        checked={checkedState}
        disabled={disabled}
        className="toggle--check-input"
        ref={input}
        type="checkbox"
        value="true"
      />
      <span className="toggle--slider" />
      {/* if we have a named input we want to add the value to the form */}
      {inputProps.name && <input type="hidden" name={inputProps.name} value={`${!!checkedState}`} />}
    </div>
  );

  if (chakra === 'off' && !label) {
    return toggle;
  }

  if (chakra === 'off') {
    return (
      <>
        <div className="toggle-checkbox-container" {...containerProps}>
          {leftToggle && toggle}
          <label className={labelClasses} htmlFor={id}>
            {label}
          </label>
          {!leftToggle && toggle}
        </div>
        {hintText && <div className="toggle-checkbox-hint-text m-t-5">{hintText}</div>}
      </>
    );
  }

  return (
    <>
      <Checkbox
        {...dataAttributes}
        isChecked={checkedState}
        isDisabled={disabled}
        name={inputProps.name}
        onChange={handleChakraClick}
        type="toggle"
        value={String(checked)}
        toggleLabelSide={leftToggle ? 'right' : 'left'}
      >
        <SelectionLabel label={label} />
      </Checkbox>
      {hintText && <div className="toggle-checkbox-hint-text">{hintText}</div>}
    </>
  );
};
